/**
 * Calendar Data Hook
 *
 * Fetches and normalizes calendar data from multiple API sources:
 * - Medications from GET /{tenantId}/patients/{patientId}/medications
 * - Appointments from GET /{tenantId}/patients/{patientId}/appointments
 * - Period data from patient record
 * - Fertile window (computed from period data)
 *
 * Returns a unified set of CalendarItems that can be rendered in the calendar UI.
 */

import { useMemo, useRef } from "react";
import { cycleKeys } from "@src/packages/cycles/cycle-cache-keys";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useApiClient } from "../api/louise-api";
import { usePatient } from "../patients/hooks/usePatient";
import { useTimezone } from "../preferences";
import { getPushToken } from "../notifications/usePushNotifications";
import {
  CalendarItem,
  DaySummary,
  ApiMedication,
  ApiAppointment,
  normalizeMedication,
  normalizeAppointment,
  createPeriodItem,
  generatePeriodDays,
  buildDaySummaryMap,
  getItemsForDate,
  getUpcomingItems,
} from "./calendar.types";
import { toLocalDateString, getToday } from "../utils/date";
import {
  mapPeriodEvidence,
  ageYearsFromDob,
} from "./prediction/evidenceMapper";
import { getFertileWindowPrediction } from "./prediction/orchestrator";
import { predictionToCalendarItems } from "./prediction/predictionToCalendarItems";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { PatientMedicationRoute } from "./medication-route";
import {
  appointmentKeys,
  calendarKeys,
  medicationDoseLogKeys,
  medicationLogKeys,
  patientMedicationKeys,
} from "./patient-cache-keys";
import { buildPatientAppointmentsRequest } from "./patient-appointment-request";
import {
  buildCreateAppointmentRequest,
  buildDeleteAppointmentRequest,
  buildUpdateAppointmentRequest,
} from "./appointment-mutation-request";
import { getMedicationLogOccurrenceKey } from "./medication-log-date";
import { fetchMedicationLogsInRange } from "./medication-log-fetch";
import {
  buildMedicationReminderPayload,
  buildMedicationReminderUpdatePayload,
} from "./medication-reminder-payload";
import { medicationOverlapsRange } from "./medication-occurrences";
import {
  buildAppointmentReminderPayload,
  findAppointmentReminders,
} from "./appointment-reminder-payload";
import { createBatchRetryLedger } from "./batch-create";
import {
  buildSymptomCreateItems,
  settleSymptomCreateBatch,
} from "./symptom-batch";
import { throwApiError } from "../api/api-error";
import { logClinicalMutationError } from "./clinical-log-redaction";
import {
  getClinicalDataAvailability,
  type ClinicalDataAvailability,
} from "./clinical-data-availability";

export { calendarKeys };

// ============================================================================
// Medication Dose Log Types
// ============================================================================

/**
 * Medication dose log status.
 */
export type MedicationDoseStatus =
  "taken" | "taken_late" | "missed" | "skipped";

/**
 * Normalized medication dose log used across the calendar/medication UI.
 * Server logs (and the medication-log endpoints) are mapped into this shape.
 */
export interface LocalMedicationDoseLog {
  id: string;
  /** The medication ID this log is for */
  medicationId: string;
  /** The scheduled date of the dose (YYYY-MM-DD) */
  scheduledDate: string;
  /** The scheduled time of the dose (HH:MM) */
  scheduledTime: string;
  /** Status of the dose */
  status: MedicationDoseStatus;
  /** Actual time taken (if taken or taken_late), ISO datetime string */
  actualTime: string | null;
  /** Optional notes */
  notes: string | null;
  createdAt: string;
}

// ============================================================================
// Feature Flags
// ============================================================================

/**
 * Feature flags for calendar data sources.
 * Set to true when the corresponding API is available and tested.
 */
const FEATURE_FLAGS = {
  /** Enable fetching medications from API */
  MEDICATIONS_API: true,
  /** Enable fetching appointments from API */
  APPOINTMENTS_API: true,
  /** Enable period days from Dashboard period logs and patient data */
  PERIOD_DATA: true,
  /** Enable fertile window computation */
  FERTILE_WINDOW: true,
  /** Enable symptoms tracking from API */
  SYMPTOMS_API: true,
};

// ============================================================================
// API Fetchers
// ============================================================================

/**
 * Fetch medications for a patient.
 */
async function fetchMedications(
  client: ReturnType<typeof useApiClient>["client"],
  tenantId: string,
  patientId: string,
): Promise<ApiMedication[]> {
  if (!FEATURE_FLAGS.MEDICATIONS_API) {
    return [];
  }

  try {
    const response = await client.GET(
      "/{tenantId}/patients/{patientId}/medications",
      {
        params: {
          path: { tenantId, patientId },
        },
      },
    );

    if (response.error) {
      throwApiError(
        response.error,
        "Failed to load medications",
        "calendar.medications",
        response.response.status,
      );
    }

    return response.data?.data ?? [];
  } catch (error) {
    throwApiError(
      error,
      "Failed to load medications",
      "calendar.medications",
      undefined,
    );
  }
}

/**
 * Fetch appointments for a date range.
 */
async function fetchAppointments(
  client: ReturnType<typeof useApiClient>["client"],
  tenantId: string,
  patientId: string,
  startDate?: string,
  endDate?: string,
): Promise<ApiAppointment[]> {
  if (!FEATURE_FLAGS.APPOINTMENTS_API) {
    return [];
  }

  try {
    const request = buildPatientAppointmentsRequest({
      tenantId,
      patientId,
      startDate,
      endDate,
    });
    const response = await client.GET(request.path, request.options);

    if (response.error) {
      throwApiError(
        response.error,
        "Failed to load appointments",
        "calendar.appointments",
        response.response.status,
      );
    }

    return response.data?.data ?? [];
  } catch (error) {
    throwApiError(
      error,
      "Failed to load appointments",
      "calendar.appointments",
      undefined,
    );
  }
}

/**
 * API symptom structure — derived from the OpenAPI schema.
 */
export type ApiSymptom = ResponseBody<
  paths["/{tenantId}/patients/{patientId}/symptoms"]["get"]
>["data"][number];

/**
 * API period-log structure — derived from the OpenAPI schema.
 */
export type ApiPeriodLog = ResponseBody<
  paths["/patients/{patientId}/periods"]["get"]
>["data"][number];

/**
 * Fetch symptoms for a date range.
 */
async function fetchSymptoms(
  client: ReturnType<typeof useApiClient>["client"],
  tenantId: string,
  patientId: string,
  date?: string,
  startDate?: string,
  endDate?: string,
): Promise<ApiSymptom[]> {
  if (!FEATURE_FLAGS.SYMPTOMS_API) {
    return [];
  }

  try {
    const response = await client.GET(
      "/{tenantId}/patients/{patientId}/symptoms",
      {
        params: {
          path: { tenantId, patientId },
          query: {
            date: date ?? undefined,
            startDate: date ? undefined : (startDate ?? undefined),
            endDate: date ? undefined : (endDate ?? undefined),
            sort: "asc",
          },
        },
        headers: {
          "Cache-Control": "no-cache",
        },
      },
    );

    if (response.error) {
      throwApiError(
        response.error,
        "Failed to load symptoms",
        "calendar.symptoms",
        response.response.status,
      );
    }

    return response.data?.data ?? [];
  } catch (error) {
    throwApiError(
      error,
      "Failed to load symptoms",
      "calendar.symptoms",
      undefined,
    );
  }
}

/**
 * Normalize API symptom to CalendarItem
 */
function normalizeSymptom(symptom: ApiSymptom): CalendarItem {
  return {
    id: symptom.id,
    type: "symptom",
    startAt: symptom.dateAndTime ?? new Date().toISOString(),
    title: symptom.symptom ?? "Symptom",
    subtitle: symptom.notes ?? undefined,
    color: "#FBBF24", // Amber
    meta: {
      symptom: symptom.symptom ?? undefined,
      symptomId: symptom.id,
      notes: symptom.notes,
      source: "patient",
    },
  };
}

/**
 * Fetch period logs for a date range.
 */
async function fetchPeriodLogs(
  client: ReturnType<typeof useApiClient>["client"],
  patientId: string,
): Promise<ApiPeriodLog[]> {
  if (!FEATURE_FLAGS.PERIOD_DATA) {
    return [];
  }

  try {
    const response = await client.GET("/patients/{patientId}/periods", {
      params: {
        path: { patientId },
      },
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (response.error) {
      throwApiError(
        response.error,
        "Failed to load period logs",
        "calendar.periods",
        response.response.status,
      );
    }

    return response.data?.data ?? [];
  } catch (error) {
    throwApiError(
      error,
      "Failed to load period logs",
      "calendar.periods",
      undefined,
    );
  }
}

// ============================================================================
// Main Hook
// ============================================================================

export interface UseCalendarDataOptions {
  /** Start date for filtering (YYYY-MM-DD). If not provided, fetches all. */
  startDate?: string;
  /** End date for filtering (YYYY-MM-DD). If not provided, fetches all. */
  endDate?: string;
  /** Whether to enable the query */
  enabled?: boolean;
}

export interface UseCalendarDataResult {
  /** All calendar items in the date range */
  items: CalendarItem[];
  /** Summary map keyed by date (YYYY-MM-DD) for quick day-level lookups */
  daySummaryMap: Map<string, DaySummary>;
  /** Items for a specific date */
  getItemsForDate: (date: string) => CalendarItem[];
  /** Upcoming items (medications and appointments) */
  upcomingItems: CalendarItem[];
  /** Loading state */
  isLoading: boolean;
  /** Background refresh state (including retries with cached data). */
  isFetching: boolean;
  /** Product-facing availability derived from data/loading/error. */
  availability: ClinicalDataAvailability;
  /** Error state */
  error: Error | null;
  /** Refetch data */
  refetch: () => void;
}

/**
 * Hook to fetch and manage calendar data.
 *
 * Combines data from multiple sources:
 * - Medications (from API)
 * - Appointments (from API)
 * - Period days (from patient data)
 * - Fertile window (computed)
 */
export function useCalendarData(
  options: UseCalendarDataOptions = {},
): UseCalendarDataResult {
  const { startDate, endDate, enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();
  const { data: patientData } = usePatient();

  // Build a fingerprint of period data so the query refetches when periods change
  const periodFingerprint = useMemo(() => {
    const mh = (patientData as Record<string, unknown> | undefined)
      ?.obGynHistory as Record<string, unknown> | undefined;
    const pd = mh?.menstrualHealth as
      Record<string, string | null | undefined> | undefined;
    if (!pd) return "none";
    return `${pd.period1StartDate ?? ""}-${pd.period1EndDate ?? ""}-${pd.period2StartDate ?? ""}-${pd.period2EndDate ?? ""}-${pd.period3StartDate ?? ""}-${pd.period3EndDate ?? ""}`;
  }, [patientData]);

  // Build query key for caching
  // Include period fingerprint so the query refetches when period data changes
  const queryKey = [
    ...calendarKeys.calendarData(
      tenantId ?? "",
      patientId ?? "",
      startDate ?? "all",
      endDate ?? "all",
    ),
    periodFingerprint,
  ];

  // Main query that fetches all calendar data
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const allItems: CalendarItem[] = [];
        const loggedPeriodDates = new Set<string>();

        // Fetch from API if we have a valid patient ID
        if (patientId) {
          // Fetch medications from API
          const medications = await fetchMedications(
            client,
            tenantId,
            patientId,
          );
          for (const med of medications) {
            // A course that started before the window but is still running
            // belongs in the window — filter by overlap, then expand each
            // in-range day via the canonical adapter (#2223).
            if (!medicationOverlapsRange(med, { startDate, endDate })) {
              continue;
            }

            const medItems = normalizeMedication(med, { startDate, endDate });
            allItems.push(...medItems);
          }

          // Fetch appointments from API
          const appointments = await fetchAppointments(
            client,
            tenantId,
            patientId,
            startDate,
            endDate,
          );
          for (const appt of appointments) {
            allItems.push(normalizeAppointment(appt));
          }

          // Fetch symptoms from API
          const symptoms = await fetchSymptoms(
            client,
            tenantId,
            patientId,
            undefined,
            startDate,
            endDate,
          );
          for (const symptom of symptoms) {
            allItems.push(normalizeSymptom(symptom));
          }

          const periodLogs = await fetchPeriodLogs(client, patientId);
          const periodLogItems = periodLogs
            .filter((periodLog) => {
              if (startDate && periodLog.date < startDate) return false;
              if (endDate && periodLog.date > endDate) return false;
              return true;
            })
            .map((periodLog) =>
              createPeriodItem(periodLog.date, periodLog.flow),
            );
          for (const item of periodLogItems) {
            loggedPeriodDates.add(item.startAt.split("T")[0]!);
          }
          allItems.push(...periodLogItems);
        }

        // Add period days from patient medical-history data.
        // Period data is nested in obGynHistory.menstrualHealth
        const menstrualHealth = (patientData as Record<string, unknown>)
          ?.obGynHistory as Record<string, unknown> | undefined;
        const periodData = menstrualHealth?.menstrualHealth as
          | {
              period1StartDate?: string | null;
              period1EndDate?: string | null;
              period2StartDate?: string | null;
              period2EndDate?: string | null;
              period3StartDate?: string | null;
              period3EndDate?: string | null;
              minAverageCycleLength?: number | null;
              maxAverageCycleLength?: number | null;
            }
          | undefined;

        if (FEATURE_FLAGS.PERIOD_DATA && periodData) {
          // Extract period dates from patient record
          const periodRanges = [
            {
              start: periodData.period1StartDate,
              end: periodData.period1EndDate,
            },
            {
              start: periodData.period2StartDate,
              end: periodData.period2EndDate,
            },
            {
              start: periodData.period3StartDate,
              end: periodData.period3EndDate,
            },
          ];

          for (const range of periodRanges) {
            if (range.start) {
              const days = generatePeriodDays(range.start, range.end ?? null);
              for (const day of days) {
                if (loggedPeriodDates.has(day)) continue;
                // Don't filter period items by date range — prediction algorithms
                // need full period history to calculate cycle length and predictions
                allItems.push(createPeriodItem(day));
              }
            }
          }
        }

        // Fertile window + next-period prediction from the evidence-aware engine.
        // Builds a normalized evidence set from the observed period items (predicted items are
        // excluded by the mapper) plus patient age and declared cycle bounds, runs the
        // orchestrator, and translates its output into calendar items. The ASRM 6-day window,
        // ovulation distribution, and uncertainty all live in src/packages/care/prediction.
        if (FEATURE_FLAGS.FERTILE_WINDOW) {
          const todayStr = toLocalDateString(getToday());
          const evidence = mapPeriodEvidence({
            items: allItems,
            ageYears: ageYearsFromDob(patientData?.dateOfBirth),
            declaredCycleLengthDays: periodData
              ? {
                  min: periodData.minAverageCycleLength ?? null,
                  max: periodData.maxAverageCycleLength ?? null,
                }
              : null,
          });
          const prediction = getFertileWindowPrediction(evidence, todayStr);
          const predictedItems = predictionToCalendarItems(
            prediction,
            todayStr,
          );
          for (const item of predictedItems) {
            const itemDate = item.startAt.split("T")[0]!;
            if (startDate && itemDate < startDate) continue;
            allItems.push(item);
          }
        }

        return allItems;
      } catch (err) {
        console.error("[cal:queryFn] ERROR:", err);
        throwApiError(
          err,
          "Failed to load calendar records",
          "calendar",
          undefined,
        );
      }
    },
    // Enable once we have a patient ID and patient data is loaded. Gating on
    // patientData avoids caching empty results before period data loads.
    enabled: enabled && !!patientId && patientData !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 0, // TODO: restore to 30 min after debugging
  });
  const items = query.data ?? [];
  const availability = getClinicalDataAvailability({
    data: query.data,
    error: query.error,
    isLoading: query.isLoading,
  });

  // Build summary map for quick lookups
  const daySummaryMap = buildDaySummaryMap(items);

  // Get upcoming items
  const now = getToday();
  const upcomingItems = getUpcomingItems(items, now, 5);

  return {
    items,
    daySummaryMap,
    getItemsForDate: (date: string) => getItemsForDate(items, date),
    upcomingItems,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    availability,
    error: query.error as Error | null,
    refetch: () => {
      void query.refetch();
    },
  };
}

// ============================================================================
// Mutation Hooks for Creating Items
// ============================================================================

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUUID(v: string | null | undefined): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

/** API enum for a patient medication's delivery form, derived from the schema. */
type PatientMedicationFormValue = NonNullable<
  NonNullable<
    paths["/{tenantId}/patients/{patientId}/medications/{medicationId}"]["patch"]["requestBody"]
  >["content"]["application/json"]["form"]
>;

export interface CreateMedicationPayload {
  name: string;
  startDate: string;
  endDate?: string | null;
  dose?: number | null;
  doseUnit?: string | null;
  timeTaken?: string[];
  notes?: string | null;
  /** Administration route (e.g., "subcutaneous", "oral") */
  route?: PatientMedicationRoute | null;
  /** Delivery form for injectable meds (e.g., "pre_filled_pen", "injection_solution") */
  form?: string | null;
  /** Optional: Link medication to a treatment cycle */
  cycleId?: string | null;
  /** Optional: Link medication to a specific treatment phase (e.g., "pre", "stim") */
  phaseId?: string | null;
  /** Optional: Link to a specific medication catalog entry */
  medicationId?: string | null;
  /**
   * Patient-facing display override, persisted server-side (#319). Catalog
   * names are never touched; readers fall back to medication.name, then
   * rawMedicationName.
   */
  displayName?: string | null;
  /**
   * Medication role for special medications, persisted verbatim by the API
   * (dashboard-api#1425). The app uses "trigger_shot" and "standard";
   * cross-surface trigger behavior still derives from phase.triggerShot.
   */
  medicationRole?: "standard" | "trigger_shot" | null;
}

async function reminderContext() {
  return {
    pushToken: (await getPushToken()) ?? null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

async function buildMedicationReminder(timeTaken: string[] | undefined) {
  if (!timeTaken || timeTaken.length === 0) return undefined;
  return buildMedicationReminderPayload(timeTaken, await reminderContext());
}

async function buildMedicationReminderUpdate(timeTaken: string[] | undefined) {
  return buildMedicationReminderUpdatePayload(
    timeTaken,
    timeTaken && timeTaken.length > 0
      ? await reminderContext()
      : { pushToken: null, timezone: "" },
  );
}

/**
 * Hook to create a new medication via the API.
 */
export function useCreateMedication() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientMedicationKey = patientMedicationKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (payload: CreateMedicationPayload) => {
      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      // Schedule a daily reminder at the medication's times. Without this the
      // backend never creates a reminder, so the notification never fires (#1332).
      // The offset (e.g. "15m before") comes from the user's notification
      // preferences server-side; we just supply the times + timezone here.
      const reminder = await buildMedicationReminder(payload.timeTaken);

      const response = await client.POST(
        "/{tenantId}/patients/{patientId}/medications",
        {
          params: {
            path: { tenantId, patientId },
          },
          body: {
            startDate: payload.startDate,
            endDate: payload.endDate,
            dose: payload.dose,
            doseUnit: payload.doseUnit,
            timeTaken: payload.timeTaken as string[] | null | undefined,
            route: payload.route,
            notes: payload.notes,
            source: "patient",
            ...(isUUID(payload.cycleId) && { cycleId: payload.cycleId }),
            ...(isUUID(payload.phaseId) && { phaseId: payload.phaseId }),
            ...(isUUID(payload.medicationId)
              ? { medicationId: payload.medicationId }
              : { rawMedicationName: payload.name }),
            ...(payload.displayName !== undefined && {
              displayName: payload.displayName,
            }),
            ...(payload.medicationRole != null && {
              medicationRole: payload.medicationRole,
            }),
            ...(payload.form && {
              form: payload.form as PatientMedicationFormValue,
            }),
            ...(reminder && { reminder }),
          },
        },
      );

      if (response.error) {
        // Redacted: the payload and raw error body carry clinical details
        // (names, doses, notes) and must not reach the console (#2247).
        logClinicalMutationError(
          "[useCreateMedication]",
          "/{tenantId}/patients/{patientId}/medications",
          response.error,
        );
        throwApiError(
          response.error,
          "Failed to save medication",
          "patient-medications",
          response.response.status,
        );
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      queryClient.refetchQueries({ queryKey: cycleKeys.phasesAll });
      // Force a refetch (not just invalidate) so screens that aren't currently
      // subscribed still see the new medication immediately when they mount.
      queryClient.refetchQueries({ queryKey: patientMedicationKey });
    },
  });
}

export interface UpdateMedicationPayload {
  medicationId: string;
  startDate?: string;
  endDate?: string | null;
  dose?: number | null;
  doseUnit?: string | null;
  timeTaken?: string[];
  notes?: string | null;
  /** Administration route; null explicitly clears the patient override. */
  route?: PatientMedicationRoute | null;
  /** Delivery form for injectable meds (e.g., "pre_filled_pen", "injection_solution") */
  form?: string | null;
  /** Patient-facing display override (#319); null explicitly clears it. */
  displayName?: string | null;
  /** Medication role, persisted verbatim; null clears it. */
  medicationRole?: string | null;
}

/**
 * Hook to update an existing medication via the API.
 */
export function useUpdateMedication() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientMedicationKey = patientMedicationKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (payload: UpdateMedicationPayload) => {
      const { medicationId, ...updates } = payload;

      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      // Re-sync the reminder when the times change so notifications track the
      // new schedule (#1332). Only send it when timeTaken was provided — a
      // partial update that doesn't touch times leaves the reminder as-is.
      const reminder =
        updates.timeTaken !== undefined
          ? await buildMedicationReminderUpdate(updates.timeTaken)
          : undefined;

      const response = await client.PATCH(
        "/{tenantId}/patients/{patientId}/medications/{medicationId}",
        {
          params: {
            path: { tenantId, patientId, medicationId },
          },
          body: {
            startDate: updates.startDate,
            endDate: updates.endDate,
            dose: updates.dose,
            doseUnit: updates.doseUnit,
            timeTaken: updates.timeTaken as string[] | null | undefined,
            notes: updates.notes,
            ...(updates.route !== undefined && { route: updates.route }),
            ...(updates.form !== undefined && {
              form: updates.form as PatientMedicationFormValue | null,
            }),
            ...(updates.displayName !== undefined && {
              displayName: updates.displayName,
            }),
            ...(updates.medicationRole !== undefined && {
              medicationRole: updates.medicationRole,
            }),
            ...(reminder && { reminder }),
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to update medication",
          "patient-medications",
          response.response.status,
        );
      }

      return response.data;
    },
    onSuccess: async (_data, variables) => {
      // Patch only this patient's Care and medication lists. Scanning the
      // entire QueryClient can mutate another patient's cached records.
      const patchMedication = (old: unknown) => {
        if (!Array.isArray(old)) return old;
        let changed = false;
        const next = old.map((item) => {
          if (
            item &&
            typeof item === "object" &&
            "id" in item &&
            (item as { id: string }).id === variables.medicationId
          ) {
            changed = true;
            return {
              ...item,
              ...(variables.endDate !== undefined && {
                endDate: variables.endDate,
              }),
              ...(variables.notes !== undefined && {
                notes: variables.notes,
              }),
              ...(variables.startDate !== undefined && {
                startDate: variables.startDate,
              }),
              ...(variables.displayName !== undefined && {
                displayName: variables.displayName,
              }),
              ...(variables.medicationRole !== undefined && {
                medicationRole: variables.medicationRole,
              }),
              ...(variables.dose !== undefined && { dose: variables.dose }),
              ...(variables.doseUnit !== undefined && {
                doseUnit: variables.doseUnit,
              }),
              ...(variables.timeTaken !== undefined && {
                timeTaken: variables.timeTaken,
              }),
            };
          }
          return item;
        });
        return changed ? next : old;
      };
      queryClient.setQueriesData<unknown>(
        { queryKey: patientCalendarKey },
        patchMedication,
      );
      queryClient.setQueriesData<unknown>(
        { queryKey: patientMedicationKey },
        patchMedication,
      );
      await queryClient.refetchQueries({ queryKey: patientCalendarKey });
      queryClient.refetchQueries({ queryKey: patientMedicationKey });
      queryClient.refetchQueries({ queryKey: ["medications"] });
      queryClient.refetchQueries({ queryKey: cycleKeys.phasesAll });
    },
  });
}

/**
 * Hook to delete a medication by ID via the API.
 */
export function useDeleteMedication() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientMedicationKey = patientMedicationKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientDoseLogKey = medicationDoseLogKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientMedicationLogKey = medicationLogKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (medicationId: string) => {
      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      const response = await client.DELETE(
        "/{tenantId}/patients/{patientId}/medications/{medicationId}",
        {
          params: {
            path: { tenantId, patientId, medicationId },
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to delete medication",
          "patient-medications",
          response.response.status,
        );
      }

      return { success: true };
    },
    onSuccess: async (_data, medicationId) => {
      // Remove the record only from this patient's Care and medication lists.
      const removeMedication = (old: unknown) => {
        if (!Array.isArray(old)) return old;
        const next = old.filter(
          (item) =>
            !(
              item &&
              typeof item === "object" &&
              "id" in item &&
              (item as { id: string }).id === medicationId
            ),
        );
        return next.length === old.length ? old : next;
      };
      queryClient.setQueriesData<unknown>(
        { queryKey: patientCalendarKey },
        removeMedication,
      );
      queryClient.setQueriesData<unknown>(
        { queryKey: patientMedicationKey },
        removeMedication,
      );
      await queryClient.refetchQueries({ queryKey: patientCalendarKey });
      queryClient.invalidateQueries({ queryKey: patientDoseLogKey });
      // The primary visible-history hook (useMedicationLogs) reads a
      // different namespace — invalidate it too or deleted medications'
      // logs linger until a full restart (#2223).
      queryClient.invalidateQueries({ queryKey: patientMedicationLogKey });
      queryClient.refetchQueries({ queryKey: patientMedicationKey });
      queryClient.refetchQueries({ queryKey: ["medications"] });
      queryClient.refetchQueries({ queryKey: cycleKeys.phasesAll });
    },
  });
}

export interface CreateAppointmentPayload {
  cycleId?: string | null;
  /**
   * Attendees besides the patient. The partner rides along on a collection that
   * is his — see `buildAppointmentParticipants`.
   */
  additionalParticipantIds?: readonly (string | null | undefined)[];
  title: string;
  description?: string | null;
  consultationType?: string[] | null;
  startDate: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  timeZone?: string;
  meetingRoom?: string | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
}

/**
 * Best-effort reminder sync for appointment create/update (#2243).
 *
 * Verified live on the dev backend: appointment CRUD creates no reminder
 * rows itself, the reminders table enforces a foreign key on appointmentId,
 * and DELETING an appointment cascades to its linked reminders — so the
 * client only syncs create and reschedule; deletion is the server's job.
 * Failures here never fail the appointment operation itself — the
 * appointment is the clinical record; the reminder is a convenience the
 * reminders screen can still repair.
 */
async function syncAppointmentReminder(args: {
  client: ReturnType<typeof useApiClient>["client"];
  tenantId: string;
  action: "create" | "update";
  appointmentId: string;
  title?: string;
  startDate?: string;
  startTime?: string | null;
  timeZone?: string;
}): Promise<void> {
  const { client, tenantId, action, appointmentId } = args;
  try {
    if (!args.startDate || !args.startTime || !args.title) return;
    const payload = buildAppointmentReminderPayload({
      appointmentId,
      title: args.title,
      startDate: args.startDate,
      startTime: args.startTime,
      timezone: args.timeZone,
      pushToken: (await getPushToken()) ?? null,
    });

    if (action === "update") {
      // Move = delete + recreate: the reminder PATCH contract takes a plain
      // date (verified live) while POST takes an instant, so recreating is
      // the schema-stable way to follow a rescheduled appointment.
      const listResponse = await client.GET("/{tenantId}/me/reminders", {
        params: { path: { tenantId } },
      });
      const linked = findAppointmentReminders(
        listResponse.data?.data,
        appointmentId,
      );
      for (const reminder of linked) {
        await client.DELETE("/{tenantId}/me/reminders/{id}", {
          params: { path: { tenantId, id: reminder.id } },
        });
      }
    }

    await client.POST("/{tenantId}/me/reminders", {
      params: { path: { tenantId } },
      body: payload,
    });
  } catch (error) {
    logClinicalMutationError(
      "[syncAppointmentReminder]",
      "/{tenantId}/me/reminders",
      error,
    );
  }
}

/**
 * Hook to create a new appointment via the API.
 */
export function useCreateAppointment() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientAppointmentKey = appointmentKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (payload: CreateAppointmentPayload) => {
      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      // Compatibility route (deprecated tenant-wide contract) — see
      // appointment-mutation-request.ts for the migration seat (#2237).
      const request = buildCreateAppointmentRequest({
        tenantId,
        patientId,
        additionalParticipantIds: payload.additionalParticipantIds,
        body: {
          cycleId: payload.cycleId,
          title: payload.title,
          description: payload.description,
          consultationType: payload.consultationType,
          startDate: payload.startDate,
          startTime: payload.startTime,
          endDate: payload.endDate,
          endTime: payload.endTime,
          timeZone:
            payload.timeZone ??
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          meetingRoom: payload.meetingRoom,
          isOnline: payload.isOnline,
          meetingLink: payload.meetingLink,
        },
      });
      const response = await client.POST(request.path, request.options);

      if (response.error) {
        // Redacted: appointment payloads carry titles, notes, and provider
        // details that must not reach the console (#2247).
        logClinicalMutationError(
          "[useCreateAppointment]",
          "/{tenantId}/appointments",
          response.error,
        );
        throwApiError(
          response.error,
          "Failed to save appointment",
          "useCreateAppointment",
          response.response.status,
        );
      }

      const created = response.data?.data;
      if (created?.id) {
        // Reminder resource is the single source of truth (#2243).
        await syncAppointmentReminder({
          client,
          tenantId,
          action: "create",
          appointmentId: created.id,
          title: payload.title,
          startDate: payload.startDate,
          startTime: payload.startTime ?? null,
          timeZone:
            payload.timeZone ??
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }

      return response.data;
    },
    onSuccess: async () => {
      // Awaited (#2242): mutateAsync must not resolve before every affected
      // cache is refreshed, so read-after-write behaves the same from every
      // entry point (Calendar, Care Review, Cycle Detail, Coming Up).
      await Promise.all([
        queryClient.refetchQueries({ queryKey: patientCalendarKey }),
        queryClient.refetchQueries({ queryKey: patientAppointmentKey }),
      ]);
    },
  });
}

export interface UpdateAppointmentPayload {
  appointmentId: string;
  cycleId?: string | null;
  /**
   * Attendees besides the patient, resent on every update.
   *
   * `participants` is a whole-list replacement in this contract, so omitting the
   * partner here does not leave him alone — it removes him. Moving the date of a
   * collection he is attending must not quietly take it off his calendar.
   */
  additionalParticipantIds?: readonly (string | null | undefined)[];
  title?: string;
  description?: string | null;
  consultationType?: string[] | null;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  timeZone?: string;
  meetingRoom?: string | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
}

/**
 * Hook to update an existing appointment.
 */
export function useUpdateAppointment() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientAppointmentKey = appointmentKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (payload: UpdateAppointmentPayload) => {
      const { appointmentId, ...updates } = payload;

      if (!tenantId) {
        throw new Error("Missing tenant ID");
      }

      // Compatibility route; the id rides through a widened param type
      // because the generated PATCH shape omits it (#2237).
      const request = buildUpdateAppointmentRequest({
        tenantId,
        patientId,
        appointmentId,
        additionalParticipantIds: updates.additionalParticipantIds,
        body: {
          cycleId: updates.cycleId,
          title: updates.title ?? "",
          description: updates.description,
          consultationType: updates.consultationType,
          startDate: updates.startDate,
          startTime: updates.startTime,
          endDate: updates.endDate,
          endTime: updates.endTime,
          timeZone: updates.timeZone,
          meetingRoom: updates.meetingRoom,
          isOnline: updates.isOnline,
          meetingLink: updates.meetingLink,
        },
      });
      const response = await client.PATCH(request.path, request.options);

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to update appointment",
          "useUpdateAppointment",
          response.response.status,
        );
      }

      if (updates.startDate && updates.startTime) {
        // Move (or backfill) the linked reminder with the appointment (#2243).
        await syncAppointmentReminder({
          client,
          tenantId,
          action: "update",
          appointmentId,
          title: updates.title,
          startDate: updates.startDate,
          startTime: updates.startTime,
          timeZone: updates.timeZone,
        });
      }

      return response.data;
    },
    onSuccess: async () => {
      // Awaited (#2242) — same read-after-write contract as create.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: patientCalendarKey }),
        queryClient.refetchQueries({ queryKey: patientAppointmentKey }),
      ]);
    },
  });
}

/**
 * Hook to delete an appointment by ID.
 */
export function useDeleteAppointment() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientAppointmentKey = appointmentKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (appointmentId: string) => {
      if (!tenantId) {
        throw new Error("Missing tenant ID");
      }

      // Compatibility route — see appointment-mutation-request.ts (#2237).
      const request = buildDeleteAppointmentRequest({
        tenantId,
        appointmentId,
      });
      const response = await client.DELETE(request.path, request.options);

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to delete appointment",
          "useDeleteAppointment",
          response.response.status,
        );
      }

      // Linked reminders are removed by the server: the reminders table's
      // appointmentId foreign key cascades on appointment delete (verified
      // live, #2243) — no client-side cleanup needed.

      return { success: true };
    },
    onSuccess: async () => {
      // Awaited (#2242) — same read-after-write contract as create.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: patientCalendarKey }),
        queryClient.refetchQueries({ queryKey: patientAppointmentKey }),
      ]);
    },
  });
}

export interface LogPeriodPayload {
  /** Date of the period day (YYYY-MM-DD) */
  date: string;
  /** Flow level for this day */
  flowLevel: "spotting" | "light" | "medium" | "heavy";
  /** Optional notes */
  notes?: string | null;
}

export interface LogPeriodResult {
  success: boolean;
  /** Where the data was persisted */
  persistedTo: "api";
  /** Message to display to the user */
  message: string;
  /** The saved data */
  data?: LogPeriodPayload;
}

/**
 * Hook to log period data.
 *
 * API-only by design (#1986): period logs must round-trip through
 * `POST /patients/{patientId}/periods` in every environment so reads
 * (`usePeriodLogs`), duplicate detection, and the clear-bleeding delete
 * branch all see what was written. No local-dev fork.
 */
export function useLogPeriod() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation<LogPeriodResult, Error, LogPeriodPayload>({
    mutationFn: async (payload: LogPeriodPayload): Promise<LogPeriodResult> => {
      if (!patientId) {
        throw new Error("Missing patient ID");
      }

      const response = await client.POST("/patients/{patientId}/periods", {
        params: { path: { patientId } },
        body: {
          date: payload.date,
          flow: payload.flowLevel,
          notes: payload.notes ?? null,
        },
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to log period",
          "useLogPeriod",
          response.response.status,
        );
      }

      return {
        success: true,
        persistedTo: "api",
        message: "Period logged successfully",
        data: payload,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
      queryClient.invalidateQueries({ queryKey: ["patient"] });
    },
  });
}

/**
 * Hook to delete a period-log day by its date (YYYY-MM-DD).
 * Backs `DELETE /patients/{patientId}/periods/{date}` — used by the
 * calendar's period-logging toggle mode when a user deselects a day.
 */
export function useDeletePeriodDayByDate() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation<{ success: true }, Error, string>({
    mutationFn: async (date: string) => {
      if (!patientId) {
        throw new Error("Missing patient ID");
      }

      const response = await client.DELETE(
        "/patients/{patientId}/periods/{date}",
        {
          params: { path: { patientId, date } },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to delete period day",
          "useDeletePeriodDayByDate",
          response.response.status,
        );
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
      queryClient.invalidateQueries({ queryKey: ["patient"] });
    },
  });
}

/**
 * Read the patient's period log.
 *
 * The counterpart `useLogPeriod` has always documented — this hook is what its
 * writes are supposed to become visible to. Until now the log could only be
 * read inside `useCalendarData`'s merged query, so any surface that assembled
 * its own calendar data (the Care screen does) could write period days and
 * never read them back.
 *
 * API-only, like the write side: a period day that lived only on the device
 * would produce a fertile-window estimate the record cannot justify.
 *
 * Unwindowed on purpose. Cycle length is the gap between consecutive starts,
 * so a model handed only the visible month would measure the wrong thing —
 * or nothing.
 */
export function usePeriodLogs(options: { enabled?: boolean } = {}) {
  const { client, tenantId, patientId } = useApiClient();
  const { enabled = true } = options;

  return useQuery<ApiPeriodLog[]>({
    queryKey: calendarKeys.periods(tenantId ?? "", patientId ?? ""),
    queryFn: async () => {
      if (!patientId) return [];
      return fetchPeriodLogs(client, patientId);
    },
    enabled: enabled && !!tenantId && !!patientId,
  });
}

// ============================================================================
// Symptom Hooks
// ============================================================================

export interface CreateSymptomPayload {
  /** Symptom name (e.g., "Bloating", "Fatigue") */
  symptom: string;
  /** ISO datetime string or date string */
  dateAndTime: string;
  /** Optional notes */
  notes?: string | null;
}

export interface CreateSymptomsPayload {
  /** Date for all symptoms (YYYY-MM-DD format) */
  date: string;
  /** List of symptom names */
  symptoms: string[];
  /** Optional notes (applies to all) */
  notes?: string | null;
}

/**
 * Hook to fetch symptoms for a specific date or date range.
 */
export function useSymptoms(
  options: {
    date?: string;
    startDate?: string;
    endDate?: string;
    enabled?: boolean;
  } = {},
) {
  const { client, tenantId, patientId } = useApiClient();
  const { date, startDate, endDate, enabled = true } = options;

  // Compute effective date range
  const effectiveStartDate = date ?? startDate;
  const effectiveEndDate = date ?? endDate;

  const queryKey = calendarKeys.symptoms(
    tenantId ?? "",
    patientId ?? "",
    effectiveStartDate,
    effectiveEndDate,
  );

  return useQuery({
    queryKey,
    queryFn: async () => {
      // API fetch
      if (tenantId && patientId) {
        const result = await fetchSymptoms(
          client,
          tenantId,
          patientId,
          date,
          date ? undefined : startDate,
          date ? undefined : endDate,
        );
        return result;
      }

      return [];
    },
    enabled: enabled && !!tenantId && !!patientId,
  });
}

/**
 * Hook to create a single symptom.
 */
export function useCreateSymptom() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (payload: CreateSymptomPayload) => {
      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      const response = await client.POST(
        "/{tenantId}/patients/{patientId}/symptoms",
        {
          params: {
            path: { tenantId, patientId },
          },
          body: {
            symptom: payload.symptom,
            dateAndTime: payload.dateAndTime,
            notes: payload.notes,
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to create symptom",
          "useCreateSymptom",
          response.response.status,
        );
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

/**
 * Hook to create multiple symptoms at once (for daily check-in).
 */
export function useCreateSymptoms() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const ledger = useRef(createBatchRetryLedger<ApiSymptom | undefined>());

  return useMutation({
    mutationFn: async (payload: CreateSymptomsPayload) => {
      const { date, symptoms, notes } = payload;
      const dateAndTime = `${date}T${new Date().toISOString().split("T")[1]}`;

      // API - create each symptom
      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      // Settle per symptom so accepted rows survive a sibling failure and a
      // retry doesn't duplicate them (#2236). Keys are content-addressed on
      // the stable date, not the volatile time-of-day.
      const items = buildSymptomCreateItems(
        date,
        symptoms.map((symptom) => ({ symptom, dateAndTime, notes })),
      );
      const created = await settleSymptomCreateBatch(
        items,
        async (body) => {
          const response = await client.POST(
            "/{tenantId}/patients/{patientId}/symptoms",
            {
              params: {
                path: { tenantId, patientId },
              },
              body,
            },
          );
          if (response.error) {
            throwApiError(
              response.error,
              "Failed to create symptom",
              "useCreateSymptoms",
              response.response.status,
            );
          }
          return response.data?.data;
        },
        ledger.current,
        (outcome) =>
          `Failed to create ${outcome.failed.length} symptoms; ${outcome.succeeded.length} saved`,
      );

      return { data: created.filter(Boolean) };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

/**
 * Hook to delete a symptom by ID.
 */
export function useDeleteSymptom() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (symptomId: string) => {
      if (!tenantId || !patientId) {
        throw new Error("Missing tenant or patient ID");
      }

      const response = await client.DELETE(
        "/{tenantId}/patients/{patientId}/symptoms/{symptomId}",
        {
          params: {
            path: { tenantId, patientId, symptomId },
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to delete symptom",
          "useDeleteSymptom",
          response.response.status,
        );
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

// ============================================================================
// Medication Dose Log Hooks
// ============================================================================

export interface LogMedicationDosePayload {
  /** The medication ID */
  medicationId: string;
  /** Human-readable medication name required by the medication-log endpoint */
  medicationName?: string;
  /** Optional dose label, e.g. "250 mcg" */
  dosage?: string | null;
  /** The scheduled date of the dose (YYYY-MM-DD) */
  scheduledDate: string;
  /** The scheduled time of the dose (HH:MM) */
  scheduledTime: string;
  /** Status of the dose */
  status: MedicationDoseStatus;
  /** Optional notes */
  notes?: string | null;
}

export interface LogMedicationDoseResult {
  success: boolean;
  /** Where the data was persisted */
  persistedTo: "local" | "api" | "none";
  /** Message to display to the user */
  message: string;
  /** The saved data */
  data?: LocalMedicationDoseLog;
}

type MedicationLogApiStatus = NonNullable<
  paths["/patients/{patientId}/medications/logs"]["post"]["requestBody"]
>["content"]["application/json"]["status"];

function medicationDoseStatusToApiStatus(
  status: MedicationDoseStatus,
): MedicationLogApiStatus {
  return status === "taken_late" ? "late" : status;
}

function apiStatusToMedicationDoseStatus(
  status: MedicationLogApiStatus,
): MedicationDoseStatus {
  return status === "late" ? "taken_late" : status;
}

function buildScheduledFor(scheduledDate: string, scheduledTime: string) {
  return `${scheduledDate}T${scheduledTime}:00`;
}

/**
 * Hook to log a medication dose (taken, taken late, missed, or skipped).
 */
export function useLogMedicationDose() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientDoseLogKey = medicationDoseLogKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientMedicationLogKey = medicationLogKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation<LogMedicationDoseResult, Error, LogMedicationDosePayload>({
    mutationFn: async (
      payload: LogMedicationDosePayload,
    ): Promise<LogMedicationDoseResult> => {
      const medicationId = payload.medicationId.trim();
      if (!medicationId) {
        throw new Error("Missing medication ID");
      }

      const actualTime =
        payload.status === "missed" || payload.status === "skipped"
          ? null
          : new Date().toISOString();

      if (!patientId) {
        throw new Error("Missing patient ID");
      }

      const scheduledFor = buildScheduledFor(
        payload.scheduledDate,
        payload.scheduledTime,
      );
      const response = await client.POST(
        "/patients/{patientId}/medications/logs",
        {
          params: { path: { patientId } },
          body: {
            patientMedicationId: medicationId,
            medicationName: payload.medicationName?.trim() || "Medication",
            dosage: payload.dosage ?? null,
            takenAt: actualTime ?? new Date().toISOString(),
            scheduledFor,
            status: medicationDoseStatusToApiStatus(payload.status),
            notes: payload.notes ?? null,
            source: "patient",
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to log medication",
          "useLogMedicationDose",
          response.response.status,
        );
      }

      const serverLog = response.data.data;
      const savedLog: LocalMedicationDoseLog = {
        id: serverLog.id,
        medicationId: serverLog.patientMedicationId ?? medicationId,
        scheduledDate: payload.scheduledDate,
        scheduledTime: payload.scheduledTime,
        status: apiStatusToMedicationDoseStatus(serverLog.status),
        actualTime:
          serverLog.status === "missed" || serverLog.status === "skipped"
            ? null
            : serverLog.takenAt,
        notes: serverLog.notes,
        createdAt: serverLog.createdAt,
      };

      return {
        success: true,
        persistedTo: "api",
        message: "Medication dose logged successfully",
        data: savedLog,
      };
    },
    onSuccess: (result) => {
      // Invalidate both calendar and dose log queries to refresh UI
      if (result.persistedTo !== "none") {
        queryClient.invalidateQueries({ queryKey: patientCalendarKey });
        queryClient.invalidateQueries({ queryKey: patientDoseLogKey });
        queryClient.invalidateQueries({ queryKey: patientMedicationLogKey });
      }
    },
  });
}

/**
 * Hook to get medication dose logs for the current calendar data.
 * Returns a map of medication dose logs keyed by "medicationId-date-time".
 */
export function useMedicationDoseLogs(
  options: {
    startDate?: string;
    endDate?: string;
    timezone?: string;
    enabled?: boolean;
  } = {},
) {
  const { startDate, endDate, timezone, enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();
  const preferredTimezone = useTimezone();
  const scheduleTimezone = timezone ?? preferredTimezone;

  return useQuery({
    queryKey: medicationDoseLogKeys.list(
      tenantId ?? "",
      patientId ?? "",
      startDate,
      endDate,
      scheduleTimezone,
    ),
    queryFn: async () => {
      if (!patientId) return [];

      const result = await fetchMedicationLogsInRange(client, patientId, {
        startDate,
        endDate,
        timezone: scheduleTimezone,
      });

      if (result.data === undefined) {
        throwApiError(
          result.error,
          "Failed to fetch medication logs",
          "useMedicationDoseLogs",
          result.status,
        );
      }

      return result.data
        .map((log): LocalMedicationDoseLog => {
          const occurrenceKey = getMedicationLogOccurrenceKey(
            log.scheduledFor ?? log.takenAt,
            scheduleTimezone,
          );
          const [scheduledDate = "", scheduledTime = ""] =
            occurrenceKey?.split("T") ?? [];

          return {
            id: log.id,
            medicationId: log.patientMedicationId ?? log.medicationName,
            scheduledDate,
            scheduledTime,
            status: apiStatusToMedicationDoseStatus(log.status),
            actualTime:
              log.status === "missed" || log.status === "skipped"
                ? null
                : log.takenAt,
            notes: log.notes,
            createdAt: log.createdAt,
          };
        })
        .filter((log) => {
          if (startDate && log.scheduledDate < startDate) return false;
          if (endDate && log.scheduledDate > endDate) return false;
          return true;
        });
    },
    enabled: enabled && !!patientId,
  });
}
